import {
  AddIcon,
  BuildingIcon,
  Button,
  Card,
  ChevronRightIcon,
  CloseIcon,
  DeleteIcon,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  DuplicateIcon,
  EditIcon,
  GridIcon,
  KeyIcon,
  Table,
  type TableColumns,
  Tag,
} from "@bp/ui-components";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "urql";
import { getFragmentData, graphql } from "../../client/gql";
import { ApplicationFormModal } from "../../components/Applications/ApplicationFormModal/ApplicationFormModal";
import { OrgPickerModal } from "../../components/Applications/OrgPickerModal/OrgPickerModal";
import { ProfilePickerModal } from "../../components/Applications/ProfilePickerModal/ProfilePickerModal";
import { ProductMark } from "../../components/brand";
import { useSetCrumbTitle } from "../../contexts/CrumbTitleContext";
import { useConfirm } from "../../hooks/useConfirm";
import prim from "../../core/styles/helpers.module.scss";
import { productIdFromName } from "../../utils/product";
import { showSuccessToast, showUserErrorToast } from "../../utils/toast";
import { isSafeHttpUrl } from "../../utils/url";
import css from "./ApplicationDetailPage.module.scss";

// Colocated fragment: everything the detail page renders for one application.
const ApplicationDetailPage_Application = graphql(`
  fragment ApplicationDetailPage_Application on Application {
    uuid
    clientId
    name
    uri
    licensedOrganizationsConnection {
      totalCount
      edges {
        properties {
          notAfter
          notBefore
          tecartContractId
        }
        node {
          uuid
          name
        }
      }
    }
    accessGrantedOrganizationsConnection {
      totalCount
    }
    accessGrantedProfilesConnection {
      totalCount
    }
    accessGrantedOrganizations {
      uuid
      name
    }
    accessGrantedProfiles {
      uuid
      firstName
      lastName
    }
  }
`);

const ApplicationDetailPage_AuthClient = graphql(`
  fragment ApplicationDetailPage_AuthClient on AuthClient {
    clientId
    clientName
  }
`);

const ApplicationDetailPageQuery = graphql(`
  query ApplicationDetailPage_Query($where: ApplicationWhere) {
    applications(where: $where) {
      ...ApplicationDetailPage_Application
      ...ApplicationFormModal_Application
    }
  }
`);

const ApplicationDetailPage_AuthClientQuery = graphql(`
  query ApplicationDetailPage_AuthClientQuery($clientId: ID!) {
    authClients(where: { clientId: $clientId }) {
      ...ApplicationDetailPage_AuthClient
    }
  }
`);

const ApplicationDetailPage_DeleteApplication = graphql(`
  mutation ApplicationDetailPage_DeleteApplication($uuid: ID!) {
    deleteApplications(where: { uuid: $uuid }) {
      nodesDeleted
    }
  }
`);

const ApplicationDetailPage_UpdateApplication = graphql(`
  mutation ApplicationDetailPage_UpdateApplication(
    $where: ApplicationWhere!
    $update: ApplicationUpdateInput!
  ) {
    updateApplications(where: $where, update: $update) {
      applications {
        ...ApplicationDetailPage_Application
      }
    }
  }
`);

function fmtDate(value?: string | null): string {
  if (!value) {
    return "—";
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString("de-DE");
}

type LicensedRow = {
  uuid: string;
  name: string;
  notBefore?: string | null;
  notAfter?: string | null;
  contract?: string | null;
};

export function ApplicationDetailPage() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [{ data }] = useQuery({
    query: ApplicationDetailPageQuery,
    variables: { where: { uuid: appId } },
  });
  const appMasked = data?.applications?.[0];
  const app = getFragmentData(ApplicationDetailPage_Application, appMasked);

  const [{ data: clientData }] = useQuery({
    query: ApplicationDetailPage_AuthClientQuery,
    variables: { clientId: app?.clientId ?? "" },
    pause: !app?.clientId,
  });
  const client = getFragmentData(ApplicationDetailPage_AuthClient, clientData?.authClients?.[0]);
  const [editing, setEditing] = useState(false);
  const [addAccessOpen, setAddAccessOpen] = useState(false);
  const [addLicensedOpen, setAddLicensedOpen] = useState(false);
  const [addProfileOpen, setAddProfileOpen] = useState(false);
  const [, deleteApplication] = useMutation(ApplicationDetailPage_DeleteApplication);
  const [, updateApplication] = useMutation(ApplicationDetailPage_UpdateApplication);
  const { confirm, ConfirmationDialog } = useConfirm();

  // biome-ignore lint/suspicious/noExplicitAny: neo4j-graphql nested connect/disconnect input
  const applyRel = async (update: any, okMsg: string): Promise<boolean> => {
    const res = await updateApplication({ where: { uuid: app?.uuid }, update });
    if (res.error) {
      showUserErrorToast({ text: "Aktualisierung fehlgeschlagen", error: res.error });
      return false;
    }
    showSuccessToast(okMsg);
    return true;
  };

  const removeLicensed = (uuid: string, name: string) =>
    applyRel(
      { licensedOrganizations: [{ disconnect: [{ where: { node: { uuid } } }] }] },
      `${name}: Lizenz entfernt`,
    );
  const removeAccessOrg = (uuid: string, name: string) =>
    applyRel(
      { accessGrantedOrganizations: [{ disconnect: [{ where: { node: { uuid } } }] }] },
      `${name}: Zugriff entzogen`,
    );
  const removeAccessProfile = (uuid: string) =>
    applyRel(
      { accessGrantedProfiles: [{ disconnect: [{ where: { node: { uuid } } }] }] },
      "Profilzugriff entzogen",
    );
  const addAccessProfile = async (uuid: string) => {
    const ok = await applyRel(
      { accessGrantedProfiles: [{ connect: [{ where: { node: { uuid } } }] }] },
      "Profilzugriff erteilt",
    );
    if (ok) {
      setAddProfileOpen(false);
    }
  };
  const addAccessOrg = async (uuid: string) => {
    const ok = await applyRel(
      { accessGrantedOrganizations: [{ connect: [{ where: { node: { uuid } } }] }] },
      "Organisation hinzugefügt",
    );
    if (ok) {
      setAddAccessOpen(false);
    }
  };
  const addLicensed = async (uuid: string, modules?: string[]) => {
    const ok = await applyRel(
      {
        licensedOrganizations: [
          {
            connect: [
              {
                where: { node: { uuid } },
                edge: modules?.length ? { licensedModules: modules } : {},
              },
            ],
          },
        ],
      },
      "Lizenz hinzugefügt",
    );
    if (ok) {
      setAddLicensedOpen(false);
    }
  };

  const onDelete = async () => {
    if (!app) {
      return;
    }
    const ok = await confirm({
      title: "Application löschen",
      message: `„${app.name}" wird entfernt. Lizenz- und Zugriffszuordnungen gehen verloren; der OAuth-Client bleibt bestehen.`,
      confirmText: "Löschen",
      cancelText: "Abbrechen",
    });
    if (!ok) {
      return;
    }
    const res = await deleteApplication({ uuid: app.uuid });
    if (res.error) {
      showUserErrorToast({ text: "Löschen fehlgeschlagen", error: res.error });
      return;
    }
    showSuccessToast("Application gelöscht");
    navigate("/applications");
  };

  useSetCrumbTitle(app?.name);

  useEffect(() => {
    const previous = document.title;
    document.title = `${app?.name ?? "Application"} · Schulsuite-Admin`;
    return () => {
      document.title = previous;
    };
  }, [app?.name]);

  const licensed: LicensedRow[] = useMemo(
    () =>
      (app?.licensedOrganizationsConnection?.edges ?? []).map((e) => ({
        uuid: e.node.uuid,
        name: e.node.name,
        notBefore: e.properties?.notBefore,
        notAfter: e.properties?.notAfter,
        contract: e.properties?.tecartContractId,
      })),
    [app],
  );

  const columns: TableColumns<LicensedRow>[] = [
    { id: "name", header: "Organisation", accessorKey: "name", size: 280 },
    { id: "from", header: "Lizenziert ab", accessorFn: (row) => fmtDate(row.notBefore), size: 130 },
    { id: "to", header: "Lizenziert bis", accessorFn: (row) => fmtDate(row.notAfter), size: 130 },
    {
      id: "contract",
      header: "Vertrags-ID",
      size: 140,
      cell: ({ row }) =>
        row.original.contract ? (
          <code className={css.codepill}>{row.original.contract}</code>
        ) : (
          <span className={prim.muted}>—</span>
        ),
    },
    {
      id: "remove",
      header: "",
      size: 56,
      cell: ({ row }) => (
        <Button
          hierarchy="tertiary"
          icon={<DeleteIcon />}
          aria-label={`${row.original.name} aus Lizenz entfernen`}
          onClick={(e) => {
            e.stopPropagation();
            void removeLicensed(row.original.uuid, row.original.name);
          }}
        />
      ),
    },
  ];

  if (!app) {
    return <p className={prim.muted}>Application nicht gefunden.</p>;
  }

  const pid = productIdFromName(app.name);
  const accessOrgs = app.accessGrantedOrganizations ?? [];
  const accessProfiles = app.accessGrantedProfiles ?? [];
  const uri = app.uri ?? "";

  const copyClientId = () => {
    void navigator.clipboard?.writeText(app.clientId);
    showSuccessToast("ClientId kopiert");
  };

  return (
    <>
      <div className={css.stack}>
        {/* ── header card ── */}
        <Card contentPadding="none">
          <div className={css.headerRow}>
            <div className={css.headerLeft}>
              <span className={css.markSquare}>
                {pid ? (
                  <ProductMark id={pid} size={26} />
                ) : (
                  <GridIcon style={{ fontSize: 24, color: "var(--color-grey)" }} />
                )}
              </span>
              <div style={{ minWidth: 0 }}>
                <div className={css.title}>{app.name}</div>
                <div className={css.metaRow}>
                  <button
                    type="button"
                    className={css.idPill}
                    title="Zum OAuth-Client"
                    onClick={() => navigate(`/auth-clients/${app.clientId}`)}
                  >
                    {app.clientId}
                  </button>
                  {uri !== "" && isSafeHttpUrl(uri) && (
                    <a
                      className={css.metaLink}
                      href={uri}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {uri}
                    </a>
                  )}
                </div>
              </div>
            </div>
            <div className={css.headerActions}>
              <Button hierarchy="secondary" icon={<EditIcon />} onClick={() => setEditing(true)}>
                Bearbeiten
              </Button>
              <Dropdown
                trigger={
                  <Button
                    hierarchy="secondary"
                    icon={<DotsHorizontalIcon />}
                    ariaLabel="Weitere Aktionen"
                  />
                }
              >
                <DropdownMenu
                  data={[
                    { label: "ClientId kopieren", onClick: copyClientId },
                    { label: "Löschen", color: "error", onClick: () => void onDelete() },
                  ]}
                />
              </Dropdown>
            </div>
          </div>
        </Card>

        {/* ── two-column body ── */}
        <div className={css.grid}>
          <div className={css.stack}>
            {/* Lizenzierte Organisationen */}
            <Card contentPadding="none">
              <div className={css.cardHead}>
                <span className={css.cardHeadTitle}>
                  <BuildingIcon style={{ fontSize: 18 }} />
                  Lizenzierte Organisationen
                  <span className={css.count}>
                    {app.licensedOrganizationsConnection?.totalCount ?? 0}
                  </span>
                </span>
                <div className={css.cardHeadActions}>
                  <Button
                    hierarchy="tertiary"
                    icon={<AddIcon />}
                    onClick={() => setAddLicensedOpen(true)}
                  >
                    Verwalten
                  </Button>
                </div>
              </div>
              {licensed.length > 0 ? (
                <Table<LicensedRow>
                  columns={columns}
                  data={licensed}
                  isDesktop
                  showSort
                  showRowHover
                  onRowClick={(_, row) => navigate(`/organizations/${row.original.uuid}`)}
                />
              ) : (
                <div className={css.bodyPad}>
                  <span className={css.empty}>Keine lizenzierten Organisationen.</span>
                </div>
              )}
            </Card>

            {/* Zugangsberechtigte Organisationen */}
            <Card contentPadding="none">
              <div className={css.bodyPad}>
                <div className={css.tagHead}>
                  <span className={css.tagHeadTitle}>
                    Zugangsberechtigte Organisationen
                    <span className={css.count}>
                      {app.accessGrantedOrganizationsConnection?.totalCount ?? 0}
                    </span>
                  </span>
                  <Button
                    hierarchy="tertiary"
                    icon={<AddIcon />}
                    onClick={() => setAddAccessOpen(true)}
                  >
                    Hinzufügen
                  </Button>
                </div>
                {accessOrgs.length > 0 ? (
                  <div className={css.tagWrap}>
                    {accessOrgs.map((o) => (
                      <span key={o.uuid} className={css.picktag}>
                        <button
                          type="button"
                          className={css.picktagLbl}
                          onClick={() => navigate(`/organizations/${o.uuid}`)}
                        >
                          {o.name}
                        </button>
                        <button
                          type="button"
                          className={css.picktagX}
                          aria-label={`Zugriff entziehen: ${o.name}`}
                          onClick={() => void removeAccessOrg(o.uuid, o.name)}
                        >
                          <CloseIcon style={{ fontSize: 11 }} />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className={css.empty}>Kein org-weiter Zugriff.</span>
                )}
              </div>
            </Card>

            {/* Zugangsberechtigte Profile */}
            <Card contentPadding="none">
              <div className={css.bodyPad}>
                <div className={css.tagHead}>
                  <span className={css.tagHeadTitle}>
                    Zugangsberechtigte Profile
                    <span className={css.count}>
                      {app.accessGrantedProfilesConnection?.totalCount ?? 0}
                    </span>
                  </span>
                  <Button
                    hierarchy="tertiary"
                    icon={<AddIcon />}
                    onClick={() => setAddProfileOpen(true)}
                  >
                    Hinzufügen
                  </Button>
                </div>
                {accessProfiles.length > 0 ? (
                  <div className={css.tagWrap}>
                    {accessProfiles.map((p) => (
                      <Tag
                        key={p.uuid}
                        value={`${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() || p.uuid}
                        closable
                        size="small"
                        onClose={() => void removeAccessProfile(p.uuid)}
                      />
                    ))}
                  </div>
                ) : (
                  <span className={css.empty}>Kein profil-spezifischer Zugriff.</span>
                )}
              </div>
            </Card>
          </div>

          <div className={css.stack}>
            {/* Verknüpfter OAuth-Client */}
            <Card contentPadding="none">
              <div className={css.cardHead}>
                <span className={css.cardHeadTitle}>
                  <KeyIcon style={{ fontSize: 18 }} />
                  Verknüpfter OAuth-Client
                </span>
              </div>
              <div
                className={css.prow}
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/auth-clients/${app.clientId}`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    navigate(`/auth-clients/${app.clientId}`);
                  }
                }}
              >
                <span className={css.omark}>
                  <KeyIcon style={{ fontSize: 15 }} />
                </span>
                <span className={css.prowMain}>
                  <div className={css.prowName}>{client?.clientName ?? app.clientId}</div>
                  <div className={css.prowMeta}>{app.clientId}</div>
                </span>
                <ChevronRightIcon className={css.prowChev} style={{ width: 15, height: 15 }} />
              </div>
            </Card>

            {/* delete */}
            <div className={css.gated}>
              <span className={css.gatedIc}>
                <DeleteIcon style={{ fontSize: 18 }} />
              </span>
              <div className={css.gatedMain}>
                <div className={css.gatedTitle}>Application löschen</div>
                <div className={css.gatedText}>Entfernt die Registrierung endgültig.</div>
              </div>
              <Button hierarchy="secondary" onClick={() => void onDelete()}>
                Löschen
              </Button>
            </div>
          </div>
        </div>
      </div>
      {editing && (
        <ApplicationFormModal
          mode="edit"
          // defined here: `app` derives from `appMasked` and the `!app` guard above returned otherwise
          application={appMasked!}
          onClose={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      )}
      {addAccessOpen && (
        <OrgPickerModal
          title="Org-weiten Zugriff hinzufügen"
          excludeUuids={accessOrgs.map((o) => o.uuid)}
          onClose={() => setAddAccessOpen(false)}
          onPick={(uuid) => void addAccessOrg(uuid)}
        />
      )}
      {addLicensedOpen && (
        <OrgPickerModal
          title="Organisation lizenzieren"
          withModules
          excludeUuids={licensed.map((l) => l.uuid)}
          onClose={() => setAddLicensedOpen(false)}
          onPick={(uuid, modules) => void addLicensed(uuid, modules)}
        />
      )}
      {addProfileOpen && (
        <ProfilePickerModal
          title="Profilzugriff erteilen"
          excludeUuids={accessProfiles.map((p) => p.uuid)}
          onClose={() => setAddProfileOpen(false)}
          onPick={(uuid) => void addAccessProfile(uuid)}
        />
      )}
      <ConfirmationDialog />
    </>
  );
}
